<template>
  <modal
    v-if="show"
    height="855px"
    width="556px"
    bodyPadding="0"
    :title="t('Bulk Edit Campaigns')"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/campaign-creation-details`"
  >
    <template #default>
      <div class="main-container">
        <ui-info class="mb-8">
          {{ t('You have selected {0} campaigns. Mark the checkboxes on the left and set up values for the parameters which should be edited in bulk.', [selectedItems.length]) }}
        </ui-info>
        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['trafficSource']}">
          <ui-checkbox v-model="checkboxState['trafficSource']" class="checkbox"></ui-checkbox>

          <ui-select
            v-model="form.trafficSource"
            class="element-container"
            :disabled="!checkboxState['trafficSource']"
            :placeholder="t('Traffic source')"
            :label="t('Traffic source')"
            :label-help="t('Select traffic source to add tokens to the Campaign URL automatically.')"
            :items="trafficSourceSelectOptionsWithNone"
            item-text="name"
            :loading="!store.cache.trafficSources.isUploaded"
            item-value="id"
            item-title="name"
            :return-object="true"
            attach
            :fetchOptions="fetchTrafficSourcesOptions"
            :error-messages="errorsDict['data.trafficSource']"
          ></ui-select>
        </div>

        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['tags']}">
          <ui-checkbox v-model="checkboxState['tags']" class="checkbox"></ui-checkbox>
          <ui-select
            planLimits="tagsCampaigns"
            v-model="form.tags"
            class="element-container"
            :disabled="!checkboxState['tags']"
            :placeholder="t('Select or type tags...')"
            :label="t('Tags')"
            :items="store.cache.tags.campaigns.data"
            item-text="name"
            :label-help="t('Add personalized tags to search and navigate easy through campaign list.')"
            multiple
            chips
            attach
            combobox
            multi-line
            :error-messages="errorsDict['data.tags']"
          >
            <template #no-data>
              <no-tag-found></no-tag-found>
            </template>
          </ui-select>
        </div>

        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['trackingDomainId']}">
          <ui-checkbox v-model="checkboxState['trackingDomainId']" class="checkbox"></ui-checkbox>
          <ui-select
            v-model="form.trackingDomainId"
            class="element-container"
            :disabled="!checkboxState['trackingDomainId']"
            :placeholder="t('Account default')"
            :label="t('Tracking domain')"
            :label-help="t('Choose domain to run your campaign. Click Domains tab in Settings to add other domains.')"
            :items="trackingDomains"
            item-text="domain"
            item-value="id"
            :return-object="false"
            attach
            :error-messages="errorsDict['data.trackingDomainId']"
          ></ui-select>
        </div>

        <div v-if="FFconversionNotificationUrlEnabled" class="d-flex items-container" :class="{'container-disabled':!checkboxState['conversionNotificationUrl']}">
          <ui-checkbox v-model="checkboxState['conversionNotificationUrl']" class="checkbox"/>
          <div class="element-container ">
            <div>
              <ui-input
                :label="t('Conversion Notification URL')"
                :label-help="t('Enter an additional URL to send conversions to another service.')"
                :disabled="!checkboxState['conversionNotificationUrl']"
                v-model="form.conversionNotificationUrl"
                :has-errors="!!errorsDict['data.conversionNotificationUrl']"
              ></ui-input>
            </div>
          </div>
        </div>

        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['uniquenessPeriod']}">
          <ui-checkbox v-model="checkboxState['uniquenessPeriod']" class="checkbox"></ui-checkbox>
          <ui-count-input
            class="element-container mb-4"
            v-model="uniquenessPeriod"
            :disabled="!checkboxState['uniquenessPeriod']"
            :min="0"
            :max="744"
            :label="t('Uniqueness period (hours)')"
            :help-tooltip="`${t('Set the period in which the traffic should be displayed as unique for the campaign.')} <br> ${t('This configuration is required to set up uniqueness rules for traffic distribution.')}`"
            :has-errors="!!errorsDict['data.uniquenessPeriod']"
            planLimits="uniquenessPeriod"
          />
        </div>

        <div class="d-flex items-container mt-8" :class="{'container-disabled':!checkboxState['postbackPercent']}">
          <ui-checkbox v-model="checkboxState['postbackPercent']" class="checkbox"></ui-checkbox>
          <div class="element-container">
            <div class="percent-input">
              <ui-input
                :label="t('Conversions Shaving % (Previously Named Send Postbacks, %)')"
                :label-help="t('By default, BeMob sends 100% of conversions to your traffic source. Use this option to send only a selected percentage of conversions if you do not want to disclose the full conversion volume. The value set here applies to the selected campaigns only.')"
                :disabled="!checkboxState['postbackPercent']"
                v-model="postbackPercent"
                planLimits="sendPostbacksCampaign"
                suffix="%"
                :max="100"
                :has-errors="!!errorsDict['data.postbackPercent']"
              ></ui-input>
            </div>
          </div>
        </div>

        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['trafficLossPercent']}">
          <ui-checkbox v-model="checkboxState['trafficLossPercent']" class="checkbox"></ui-checkbox>
          <div class="element-container">
            <div class="percent-input">
              <ui-input
                :label="`${t('Traffic loss')}, %`"
                :label-help="t('Traffic loss % set at the level of campaign will override the % in the traffic source settings.')"
                v-model="trafficLossPercent"
                planLimits="trafficLossCampaign"
                :disabled="!checkboxState['trafficLossPercent']"
                suffix="%"
                :max="100"
                :has-errors="!!errorsDict['data.trafficLossPercent']"
              ></ui-input>
            </div>
          </div>
        </div>

        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['sendCustomConversion']}">
          <ui-checkbox v-model="checkboxState['sendCustomConversion']" class="checkbox"></ui-checkbox>
          <div class="element-container ">
            <div>
              <ui-label
                class="mb-4"
                :text="t('Send Custom Conversions')"
                :help-tooltip="t('This option allows sending the particular custom event to the side of the traffic source.')"
              ></ui-label>

              <ui-radio-group v-model="form.sendCustomConversionMode" styleType="inline" class="mb-4">
                <ui-radio
                  :helpTooltip="t('All the custom conversions will be sent except the conversions for which sending via postback is not allowed on the level of global custom conversions settings.')"
                  class="pr-12"
                  value="all"
                  :disabled="!checkboxState['sendCustomConversion']"
                  :label="t('All')"/>
                <ui-radio
                  :helpTooltip="t('Mark the conversions you wish to send to the traffic source for this particular campaign. Only marked conversions will be sent.')"
                  class="pr-12"
                  value="include"
                  :disabled="!checkboxState['sendCustomConversion']"
                  :click="onSendCustomConversionsClick"
                  :label="t('Only Selected')"/>
                <ui-radio
                  :helpTooltip="t('Mark the conversions you don’t wish to send to the traffic source for this particular campaign. Marked conversions will not be sent.')"
                  value="exclude"
                  :disabled="!checkboxState['sendCustomConversion']"
                  :click="onSendCustomConversionsClick"
                  :label="t('Except Selected')"/>
              </ui-radio-group>

              <ui-select
                v-if="form.sendCustomConversionMode!=='all'"
                v-model="form.sendCustomConversionIds"
                :items="customConversions"
                item-text="name"
                :disabled="!checkboxState['sendCustomConversion']"
                item-value="id"
                :hide-details="2"
                :return-object="false"
                multiple
                chips
                attach
                multi-line
              ></ui-select>

              <ui-checkbox
                v-if="form.sendCustomConversionMode==='all' || form.sendCustomConversionMode==='exclude'"
                v-model="form.doNotSendNotSpecifiedConversions"
                :disabled="!checkboxState['sendCustomConversion']"
                :label="t('Do not send conversions with Not Specified status')"
                :help-tooltip="t('This option allows to limit the sending of custom conversions with \'Not Specified\' status to the traffic  source associated with this campaign.')"
              />
            </div>
          </div>
        </div>

        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['userAgentClientHintsEnabled']}">
          <ui-checkbox v-model="checkboxState['userAgentClientHintsEnabled']" class="checkbox"/>
          <div class="element-container ">
            <div>
              <ui-select
                :label="t('Use Client Hints')"
                :label-help="t('Enable to collect additional data on OS, browser version, and device type.<br>This feature may slow down redirects. You can activate it for a specific campaign or globally in General → Tracking OS, Browser and Device Data → Use Client Hints.')"
                :disabled="!checkboxState['userAgentClientHintsEnabled']"
                v-model="form.userAgentClientHintsEnabled"
                :items="useClientHintsItems"
                :return-object="false"
                :hide-details="2"
              />
            </div>
          </div>
        </div>

        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['botFiltersMode']}">
          <ui-checkbox v-model="checkboxState['botFiltersMode']" class="checkbox"/>
          <div class="element-container ">
            <div>
              <ui-select
                :label="t('Use Bot Filters')"
                :label-help="t('Allows disabling global bot filters for this campaign.')"
                :disabled="!checkboxState['botFiltersMode']"
                v-model="form.botFiltersMode"
                :items="botFiltersModeItems"
                :return-object="false"
                :hide-details="2"
              />
            </div>
          </div>
        </div>
        <div class="d-flex items-container" :class="{'container-disabled':!checkboxState['currencyId']}">
          <ui-checkbox v-model="checkboxState['currencyId']" class="checkbox"></ui-checkbox>
          <div class="element-container">
            <ui-select
              v-model="form.currencyId"
              :disabled="!checkboxState['currencyId']"
              :placeholder="t('Currency')"
              :label="t('Currency')"
              :label-help="t('Select the currency of the traffic cost. Campaign currency will be automatically converted to the account currency selected with registration.')"
              :items="currencyListOrderedPreferred"
              item-text="name"
              item-value="code"
              :return-object="false"
              :hide-details="2"
              attach
              :menu-props="{ 'maxHeight': 190 }"
              :error-messages="errorsDict['data.currencyId']"
            >
            </ui-select>
            <cost-model
              :currency-id="form.currencyId"
              v-model:cost-model="form.costModel"
              v-model:cost-value="form.costValue"
              :maximum-cost-available="true"
              v-model:max-cost-enabled="form.maxCostEnabled"
              v-model:max-cost-usd="form.maxCostUsd"
              v-model:max-cost-action="form.maxCostAction"
              v-model:max-cost-bot-offer-id="form.maxCostBotOfferId"
              :offer-options="offerOptions"
              :fetch-offer-options="fetchOfferOptions"
              :errors="errorsDict['data.costValue']"
              :max-cost-errors="errorsDict['data.maxCostUsd']"
              :max-cost-bot-offer-errors="errorsDict['data.maxCostBotOfferId']"
              :disabled="!checkboxState['currencyId']"
            ></cost-model>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <section class="justify-space-between align-center xs12" style="display: flex; width: 100%">
        <div>
          <error-aggregator v-model="errors" v-model:active="errorsControlActive"></error-aggregator>
        </div>

        <div class="d-flex justify-end align-center">
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            @click="checkClose"
          ></ui-button>

          <ui-button
            data-action="save"
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            @click="submit"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import NoTagFound from '@/views/reports/views/no-tag-found.vue';
import CostModel from '@/views/reports/forms/campaign/components/cost-model.vue';
import { useCampaignBulkEdit } from './useCampaignBulkEdit';

const emit = defineEmits<{
  close: [];
}>();

const {
  t,
  locale,
  store,

  show,
  form,
  checkboxState,
  selectedItems,
  trafficSourceSelectOptionsWithNone,
  fetchTrafficSourcesOptions,
  trackingDomains,
  FFconversionNotificationUrlEnabled,
  uniquenessPeriod,
  postbackPercent,
  trafficLossPercent,
  customConversions,
  useClientHintsItems,
  botFiltersModeItems,
  currencyListOrderedPreferred,
  offerOptions,
  fetchOfferOptions,
  errors,
  errorsDict,
  errorsControlActive,
  onSendCustomConversionsClick,
  checkClose,
  submit,
} = useCampaignBulkEdit(() => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.container-disabled {
  .element-container {
    opacity: 0.7;

    :deep(.v-input--disabled),
    :deep(.v-field--disabled) {
      opacity: 1;
    }
  }
}

.info-label {
  font-weight: bold!important;
  color: var(--color-graphite)!important;
  left: 0!important;
}

.main-container {
  flex-direction: column;
  padding: 16px;
  overflow-y: auto!important;
  height: 100%;
}

.element-container {
  margin-top: 3px;
  max-width: calc(100% - 24px)!important;
  width: 100%;
}

.items-container {
  display: flex;
  align-items: flex-start;
  flex-grow: 0;
}

.checkbox {
  margin-right: 4px;
  padding-top: 5px;
  flex-grow: 0!important;
}

.percent-input {
  width: 100%;

  :deep(.ui-input__wrapper) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    max-width: 100%;
  }

  :deep(.ui-label) {
    display: block;
    max-width: 100%;
  }
}
</style>
